// Assistant-to-Doctor notifications - floating dialogs at bottom-right
// Each notification = one dialog; multiple stack vertically
// When doctor reads, document is deleted from Firestore
import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playBeep = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    playBeep(880, 0, 0.15);
    playBeep(1100, 0.18, 0.15);
    playBeep(1320, 0.36, 0.25);
  } catch {
    // Audio not supported — silent fail
  }
};

interface AssistantNotification {
  id: string;
  message: string;
  assistant_name?: string;
  assistant_id?: string;
  clinic_name?: string;
  createdAt?: any;
}

interface AssistantNotificationsPanelProps {
  doctorId: string | null;
  isRTL?: boolean;
}

const AssistantNotificationsPanel: React.FC<AssistantNotificationsPanelProps> = ({ doctorId, isRTL = false }) => {
  const [notifications, setNotifications] = useState<AssistantNotification[]>([]);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!doctorId) {
      setNotifications([]);
      return;
    }

    isInitialLoad.current = true;
    const notifRef = collection(db, 'assistant_doctor_notifications');
    const q = query(
      notifRef,
      where('doctor_id', '==', doctorId),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as AssistantNotification));
        setNotifications(list);

        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added' && !isInitialLoad.current) {
            playNotificationSound();
          }
        });
        isInitialLoad.current = false;
      },
      (err) => {
        console.error('Assistant notifications listener error:', err);
      }
    );

    return () => unsubscribe();
  }, [doctorId]);

  const handleRead = async (notifId: string) => {
    try {
      await deleteDoc(doc(db, 'assistant_doctor_notifications', notifId));
    } catch (err) {
      console.error('Error removing notification:', err);
    }
  };

  if (!doctorId || notifications.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        ...(isRTL ? { left: 24 } : { right: 24 }),
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 12,
        maxWidth: 360,
      }}
    >
      {notifications.map((item) => (
        <div
          key={item.id}
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(220,38,38,0.25), 0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #dc2626',
            padding: 16,
            width: 320,
            direction: 'rtl',
            animation: isRTL ? 'slideInLeft 0.3s ease-out' : 'slideInRight 0.3s ease-out',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BellOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 4 }}>
                {item.assistant_name || 'المساعد'}
              </div>
              <div style={{ fontSize: 14, color: '#333', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {item.message}
              </div>
            </div>
          </div>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleRead(item.id)}
            style={{ width: '100%', background: '#dc2626', borderColor: '#dc2626' }}
          >
            تم
          </Button>
        </div>
      ))}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default AssistantNotificationsPanel;
